const emptyAccess = () => ({ canAccessAllBranches: false, accessibleBranchIds: [] });
const stringId = (value) => (value === null || value === undefined ? null : String(value));
const normalizeBranches = (branches) => (Array.isArray(branches) ? branches : []).map((branch) => ({
  ...branch,
  id: stringId(branch.id),
}));
const accessibleTenantBranches = (state) => {
  if (state.branchAccess.canAccessAllBranches) return state.tenantBranches;
  const allowed = new Set(state.branchAccess.accessibleBranchIds.map(String));
  return state.tenantBranches.filter((branch) => allowed.has(branch.id));
};

export const branchContextGetters = {
  tenantBranches: (state) => state.tenantBranches,
  accessibleBranches: (state) => accessibleTenantBranches(state),
  tenantBranchCount: (state) => state.tenantBranches.length,
  soleTenantBranchId: (state) => (state.tenantBranches.length === 1
    ? state.tenantBranches[0].id : null),
  soleAccessibleBranchId: (state, getters) => (getters.accessibleBranches.length === 1
    ? getters.accessibleBranches[0].id : null),
  isMultiBranchTenant: (state, getters, rootState) => {
    const tenantSetting = rootState.settings && rootState.settings.tenantSetting;
    const features = (tenantSetting && tenantSetting.tenantActiveFeatures) || [];
    return !!(tenantSetting
      && tenantSetting.allowMultipleBranches
      && features.includes('multiple-branches')
      && getters.tenantBranchCount > 1);
  },
  isSingleBranchTenant: (state, getters) => !getters.isMultiBranchTenant,
  activeBranchId: (state, getters, rootState) => stringId(
    rootState.auth && rootState.auth.activeBranchId,
  ),
  // Page-level branch UI follows tenant multiplicity. A user limited to one branch inside a
  // multi-branch tenant must still retain the existing multi-branch experience and semantics.
  showBranchChoice: (state, getters) => getters.isMultiBranchTenant,
  showBranchColumn: (state, getters) => getters.isMultiBranchTenant,
  showBranchDetails: (state, getters) => getters.isMultiBranchTenant,
};

export default {
  state: {
    tenantBranches: [],
    branchAccess: emptyAccess(),
    initialized: false,
  },
  getters: branchContextGetters,
  mutations: {
    setBranchContext(state, { tenantBranches, branchAccess }) {
      state.tenantBranches = normalizeBranches(tenantBranches);
      state.branchAccess = {
        canAccessAllBranches: !!branchAccess.canAccessAllBranches,
        accessibleBranchIds: (branchAccess.accessibleBranchIds || []).map(String),
      };
      state.initialized = true;
    },
    clearBranchContext(state) {
      state.tenantBranches = [];
      state.branchAccess = emptyAccess();
      state.initialized = false;
    },
  },
  actions: {
    initializeBranchContext({ state, commit, rootState }, { access, branchesResponse }) {
      if (state.initialized) return;
      const branchAccess = {
        canAccessAllBranches: !!access.canAccessAllBranches,
        accessibleBranchIds: (access.accessibleBranchIds || []).map(String),
      };
      const tenantBranches = normalizeBranches(
        branchesResponse && Array.isArray(branchesResponse.data)
          ? branchesResponse.data : branchesResponse,
      );

      commit('setBranchContext', { tenantBranches, branchAccess });
      commit('changeBranchAccess', branchAccess, { root: true });

      const activeBranchId = stringId(rootState.auth.activeBranchId);
      const branchExists = (id) => tenantBranches.some((branch) => branch.id === id);
      const isAccessible = branchAccess.canAccessAllBranches
        || branchAccess.accessibleBranchIds.includes(activeBranchId);
      // An empty tenant list means the lookup returned no usable data. Preserve the persisted
      // branch rather than widening scope; this matches the switcher's previous failure behavior.
      const branchWasRemoved = tenantBranches.length > 0 && !branchExists(activeBranchId);
      if (activeBranchId && (branchWasRemoved || !isAccessible)) {
        commit('changeActiveBranch', null, { root: true });
      } else if (!activeBranchId && access.activeBranchId) {
        commit('changeActiveBranch', stringId(access.activeBranchId), { root: true });
      }
    },
  },
};
